import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { PublicLayout } from "@/components/PublicLayout";
import { Crumbs, ArticleCard } from "@/components/content/ContentBits";
import { useContent, CATEGORY_LABELS } from "@/content/store";

const HUBS = {
  guide: {
    label: "Guides", h1: "Etsy SEO guides for big shops",
    intro: "Practical, rule-checked guides on Etsy titles, the 13 tags, attributes, descriptions, seasonal timing and editing hundreds of listings without breaking what already sells.",
    filters: ["basics", "big-catalogs", "pod", "vintage", "seasonal", "tools"],
  },
  tags: {
    label: "Etsy Tags", h1: "Etsy tags by product",
    intro: "Ready-to-adapt 13-tag sets, example titles and attribute checklists for the products Etsy sellers list most. Every tag is 20 characters or fewer.",
    filters: ["pod", "vintage", "handmade"],
  },
};

const PER_PAGE = 24;

const Pager = ({ base, page, pages }) => pages > 1 && (
  <nav aria-label="Pagination" data-testid="hub-pagination" className="mt-10 flex flex-wrap items-center gap-2 text-sm">
    {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
      <Link key={n} to={n === 1 ? base : `${base}/page/${n}`} aria-current={n === page ? "page" : undefined} data-testid={`hub-page-${n}`}
        className={`inline-flex h-10 min-w-[40px] items-center justify-center rounded-full border px-3 ${n === page ? "border-[#E9C98B]/60 text-[#F3DDAE]" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{n}</Link>
    ))}
    {page < pages && <Link to={`${base}/page/${page + 1}`} rel="next" data-testid="hub-next" className="ml-2 text-[#5EE6D0] underline underline-offset-4">Next page</Link>}
  </nav>
);

export default function ContentHub({ type }) {
  const h = HUBS[type];
  const base = type === "guide" ? "/blog" : "/etsy-tags";
  const raw = useParams().page;
  const page = raw ? parseInt(raw, 10) : 1;
  const data = useContent(`/content?type=${type}`, true);
  const [cat, setCat] = useState("all");
  const all = (data?.items || []).filter((a) => cat === "all" || a.category === cat);
  const pages = Math.max(1, Math.ceil(all.length / PER_PAGE));
  const items = all.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  if (raw === "1") return <Navigate to={base} replace />;
  if (raw && !/^[1-9]\d*$/.test(raw)) return <NotFound />;
  if (data?.items && page > pages) return <NotFound />;
  return (
    <PublicLayout>
      <div data-testid={`hub-${type}`} className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14">
        <Crumbs testid="hub-breadcrumbs" items={[["Home", "/"], [h.label]]} />
        <h1 data-testid="hub-h1" className="max-w-3xl font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{h.h1}</h1>
        <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{h.intro}</p>
        <div role="group" aria-label="Filter by category" className="mt-10 flex flex-wrap gap-2">
          {["all", ...h.filters].map((c) => (
            <button key={c} type="button" onClick={() => setCat(c)} data-testid={`hub-filter-${c}`} aria-pressed={cat === c}
              className={`min-h-[40px] rounded-full border px-4 text-sm transition-colors ${cat === c ? "border-[#E9C98B]/60 bg-[#E9C98B]/10 text-[#F3DDAE]" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
              {c === "all" ? "All" : CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
        <h2 className="sr-only">{h.label}</h2>
        {!data ? <div className="mt-8 h-64 rounded-2xl shimmer" /> : (
          <div data-testid="hub-list" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((a) => <ArticleCard key={a.slug} a={a} testid={`hub-card-${a.slug}`} />)}
            {!items.length && <p data-testid="hub-empty" className="text-muted-foreground">New pages in this category are on the way.</p>}
          </div>
        )}
        <Pager base={base} page={page} pages={pages} />
      </div>
    </PublicLayout>
  );
}
